import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import { ReferenceMaterialRegistry } from '../registries/ReferenceMaterialRegistry';
import { CustomRendererMode, type EnhancedToolResult } from '../types';

import { ConversationTool } from './ConversationTool';
import type { ToolConstructorArgs } from './Tool';
import { AI_VOICEOVER_DISABLED_MESSAGE } from './constants';

import type { ToolDefinition, ToolExecutionContext } from 'js/src/agent/types';
import { ApiService, AspectRatio, NarrativeThreadEnum, type VideoPlanRequest } from 'js/src/client';
import { VideoPlanEditorRenderer } from 'js/src/components/video-planner/VideoPlanEditorRenderer';
import { getCanUseAIVoiceover } from 'js/src/data/account';
import {
    type AutopickType,
    fetchAutopickBlueprint,
} from 'js/src/data/blueprints/blueprints-operations';
import {
    clearMediaGuidanceOverride,
    selectMediaOptions,
} from 'js/src/data/media-recommendation-preferences';
import { selectSketchControlsBrandKitId } from 'js/src/data/sketch-controls/sketch-controls-selectors';
import { isApiError } from 'js/src/types';

const MESSAGE_DEFAULT =
    "Here's your video plan. Adjust the script or settings if needed, then click Create video.";

export const createVideoFromScriptSchema = z.object({
    title: z.string().describe('Title of the video'),
    script: z
        .string()
        .describe(
            'Exact script to use for the video. This will be read or displayed as text to users, so should only contain the actual narration/text content, NOT instructions or guidance.',
        ),
    video_prompt: z.string().optional().describe('Video generation prompt (optional)'),
    aspect_ratio: z.enum(AspectRatio).describe('Aspect ratio for the video'),
    // The allowed template ids are per-instance (the templates available to this agent),
    // so the enum is injected in extendParameters rather than declared here.
    template_id: z
        .string()
        .describe(
            'Template ID to use from the available templates. MUST be one of the templates listed in the system message. Never use a non-existent template name.',
        ),
    narrative_thread: z
        .enum([NarrativeThreadEnum.TEXT, NarrativeThreadEnum.VOICEOVER])
        .describe('Narrative thread type'),
    ai_voice: z
        .string()
        .optional()
        .describe(
            'AI voice slug to use for voiceover videos (optional, only valid when narrative_thread is "voiceover", make sure to pick a specified voice ID and not make one up)',
        ),
    // Below-min values are clamped up to the minimum by preprocessArgs before validation, so
    // .min() never rejects — it's here to emit `minimum` in the schema. The per-instance
    // maximum (and its mention in the description) is injected in extendParameters.
    duration_target_seconds: z
        .number()
        .min(10)
        .optional()
        .describe('Target duration in seconds (optional, min 10)'),
    blueprint_id: z
        .number()
        .optional()
        .describe(
            "Blueprint ID to use to create a video matching the specified blueprint's style, pacing, and structure (optional, ALWAYS specify if user requests it)",
        ),
    message: z.string().meta({
        description:
            "This message is shown to the user above the video plan. Translate the following sentence into the language the user is writing in. The sentence: 'Here's your video plan. Adjust the script or settings if needed, then click Create video.'",
        default: MESSAGE_DEFAULT,
    }),
});
export type CreateVideoFromScriptArgs = z.infer<typeof createVideoFromScriptSchema>;

interface CreateVideoFromScriptToolConstructorArgs extends ToolConstructorArgs {
    templateIds?: string[];
    maxDurationSeconds?: number;
}

/**
 * Tool for creating a video from a user-provided script
 * Validates the plan and kicks off video generation in Redux
 */
export class CreateVideoFromScriptTool extends ConversationTool<
    typeof createVideoFromScriptSchema,
    CreateVideoFromScriptToolConstructorArgs
> {
    protected override readonly schema = createVideoFromScriptSchema;
    protected override readonly toolName = TOOL_NAMES.createVideoFromScript;
    protected override readonly toolDescription =
        'Create a video using an exact script provided by the user. The script will be displayed as text to users, so it should NOT contain instructions, guidance, or blocking text - only the actual narration/text content. Use this tool only when the user explicitly provides a script.';

    public override canRunAsync = true;
    public override customRendererMode = CustomRendererMode.FULL;
    override customRenderer = VideoPlanEditorRenderer;
    private templateIds: string[];
    private minDurationSeconds: number;
    private maxDurationSeconds?: number;

    public constructor(args: CreateVideoFromScriptToolConstructorArgs) {
        super(args);
        this.templateIds = args.templateIds || [];
        this.minDurationSeconds = 10;
        this.maxDurationSeconds = args.maxDurationSeconds;
    }

    // The template ids available to this agent, and this account's max video duration, are
    // per-instance — the module-level schema can't see them, so inject them here.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        if (this.templateIds.length > 0) {
            (parameters.properties.template_id as { enum?: string[] }).enum = this.templateIds;
        }
        if (this.maxDurationSeconds !== undefined) {
            const duration = parameters.properties.duration_target_seconds as {
                maximum?: number;
                description?: string;
            };
            duration.maximum = this.maxDurationSeconds;
            duration.description = `Target duration in seconds (optional, min 10, max ${this.maxDurationSeconds})`;
        }
        return parameters;
    }

    getToolDisplayName(): string {
        return 'Creating video outline';
    }

    protected override preprocessArgs(args: CreateVideoFromScriptArgs): CreateVideoFromScriptArgs {
        // message is required in the schema (with a default), but the model can omit it. Fill the
        // default here — preprocessArgs runs before validation — so a missing message doesn't hard-
        // fail, matching the Ajv useDefaults behavior the legacy (pre-Zod) path provided.
        if ((args as { message?: string }).message === undefined) {
            args = { ...args, message: MESSAGE_DEFAULT };
        }
        if (typeof args.duration_target_seconds === 'number') {
            const min = this.minDurationSeconds;
            const max = this.maxDurationSeconds;
            if (args.duration_target_seconds < min) {
                args = { ...args, duration_target_seconds: min };
            } else if (max !== undefined && args.duration_target_seconds > max) {
                args = { ...args, duration_target_seconds: max };
            }
        }
        return args;
    }

    public get statusIcon(): string {
        return 'movie';
    }

    async execute(
        args: CreateVideoFromScriptArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const cannotUseVoiceover =
            args.narrative_thread === NarrativeThreadEnum.VOICEOVER &&
            !getCanUseAIVoiceover(this._getState());
        if (cannotUseVoiceover) {
            return {
                result: `${AI_VOICEOVER_DISABLED_MESSAGE} Or use narrative_thread "text" instead.`,
                isSuccess: false,
            };
        }

        // Validate ai_voice usage
        if (args.ai_voice && args.narrative_thread !== NarrativeThreadEnum.VOICEOVER) {
            return {
                result: 'ai_voice parameter can only be used when narrative_thread is "voiceover"',
                isSuccess: false,
            };
        }

        // Validate reference material exists
        const referenceMaterialRegistry = ReferenceMaterialRegistry.getInstance();
        if (!referenceMaterialRegistry.hasContent()) {
            return {
                result: 'No reference material available from the conversation to inform video creation.',
                isSuccess: false,
            };
        }

        // Autopick blueprint if not provided (unless opted out)
        let blueprintId = args.blueprint_id;
        let blueprintAutopicked = false;
        let blueprintAutopickType: AutopickType | 'user_provided' = 'user_provided';

        const autoBlueprintRecommendationsEnabled =
            window.SERVER?.USER?.capabilities?.auto_blueprint_recommendations_enabled ?? true;
        if (!blueprintId && autoBlueprintRecommendationsEnabled) {
            const autopickResult = await fetchAutopickBlueprint({
                templateId: args.template_id,
                narrativeThread: args.narrative_thread,
                inputContent: args.script,
            });

            blueprintId = autopickResult.blueprintId ?? undefined;
            blueprintAutopicked = true;
            blueprintAutopickType = autopickResult.pickType;

            if (autopickResult.error) {
                console.warn('Blueprint autopick failed:', autopickResult.error);
            }
        }

        // Append media guidance to video_prompt if set, then clear the transient override
        const mediaOptions = selectMediaOptions(this._getState());
        this._dispatch(clearMediaGuidanceOverride());
        const mediaGuidance = mediaOptions.media_guidance?.trim();
        let videoPrompt = args.video_prompt;
        if (mediaGuidance) {
            videoPrompt = videoPrompt
                ? `${videoPrompt}\n\nMedia guidance: ${mediaGuidance}`
                : `Media guidance: ${mediaGuidance}`;
        }

        // Create video plan
        const { message, ...planArgs } = args;
        const videoPlan = {
            ...planArgs,
            video_prompt: videoPrompt,
            blueprint_id: blueprintId,
            id: undefined,
            content_prompt: undefined, // content_prompt must be undefined when specifying script
        };

        // Validate video plan using backend validation
        try {
            const validationResponse = await ApiService.sketchValidateVideoPlansCreate({
                body: {
                    plans: [videoPlan as VideoPlanRequest],
                },
            });

            if (!validationResponse.data.valid) {
                return {
                    result: `Video plan validation failed: ${validationResponse.data.errors.join(', ')}`,
                    isSuccess: false,
                };
            }

            // Use the validated plans from the response
            const validatedPlans = validationResponse.data.validated_plans;

            if (!validatedPlans || validatedPlans.length === 0) {
                return {
                    result: 'No validated plans returned from server',
                    isSuccess: false,
                };
            }

            const brandKitId = selectSketchControlsBrandKitId(this._getState());
            analytics.track('Sketch: Video Plan Generated', {
                from_script: true,
                plan_title: args.title,
                template_id: args.template_id,
                aspect_ratio: args.aspect_ratio,
                narrative_thread: args.narrative_thread,
                ai_voice: args.ai_voice,
                duration_target_seconds: args.duration_target_seconds,
                blueprint_id: blueprintId,
                blueprint_autopicked: blueprintAutopicked,
                blueprint_autopick_type: blueprintAutopickType,
                brand_kit_id: brandKitId,
            });

            return {
                result: JSON.stringify({
                    videoPlan,
                }),
                isSuccess: true,
                stopConversation: true,
            };
        } catch (error) {
            let errorMessage = 'Unknown validation error occurred';
            if (isApiError(error)) {
                errorMessage = `${error.message}: ${JSON.stringify(error.body)}`;
            }
            return {
                result: errorMessage,
                isSuccess: false,
            };
        }
    }
}
